/**
 * Audio Recorder utility for recording audio from microphone
 * Supports PC, iOS and Android devices
 */

import { apiService } from './apiService';

class AudioRecorder {
    constructor() {
        this.mediaRecorder = null;
        this.audioChunks = [];
        this.stream = null;
        this.isRecording = false;
    }

    /**
     * Starts or stops audio recording
     * @param {string} microphoneButtonId - ID of the microphone button element
     * @param {string} spinnerContainerId - ID of the spinner container element
     * @param {string} textareaId - ID of the textarea to populate with transcribed text
     * @returns {Promise} - Resolves when recording stops with the transcribed text
     */
    toggleRecording(microphoneButtonId, spinnerContainerId, textareaId) {
        return new Promise(async (resolve, reject) => {
            try {
                const microphoneButton = document.getElementById(microphoneButtonId);
                const spinner = document.getElementById(spinnerContainerId);
                
                if (spinner) {
                    spinner.style.display = "inline-block";
                }

                if (!this.isRecording) {
                    // 🟢 Start recording
                    try {
                        this.audioChunks = [];
                        
                        const constraints = {
                            audio: {
                                echoCancellation: true,
                                noiseSuppression: true,
                                autoGainControl: true
                            }
                        };
                        
                        this.stream = await navigator.mediaDevices.getUserMedia(constraints);
                        
                        this.mediaRecorder = new MediaRecorder(this.stream);
                        
                        this.mediaRecorder.ondataavailable = (event) => {
                            if (event.data.size > 0) {
                                this.audioChunks.push(event.data);
                            }
                        };
                        
                        this.mediaRecorder.onstop = async () => {
                            const audioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });
                            
                            try {
                                // Convert to WAV format
                                const wavBlob = await this.convertToWav(audioBlob);
                                
                                // Stop all tracks to release microphone
                                if (this.stream) {
                                    this.stream.getTracks().forEach(track => track.stop());
                                }
                                
                                // Send to API for transcription
                                const formData = new FormData();
                                formData.append('audio', wavBlob, 'recording.wav');
                                
                                // Get accountId from localStorage
                                const accountId = localStorage.getItem('accountId');
                                if (accountId) {
                                    formData.append('accountId', accountId);
                                }
                                
                                // Call the API
                                console.log('Sending audio data to server...');
                                
                                // Get authentication token from localStorage
                                const authToken = localStorage.getItem('installamelo-auth-token');
                                
                                // Define headers with authentication token
                                const options = authToken ? {
                                    headers: {
                                        'auth-token': authToken
                                    }
                                } : {};
                                
                                // Use the explicit base URL from environment variable
                                const apiUrl = `${import.meta.env.VITE_API_BASE_URL}/openAI/speechToText`;
                                console.log('Sending audio to speech-to-text API at:', apiUrl);
                                const response = await apiService.postFormData(apiUrl, formData, options);
                                
                                console.log('Server response:', response);
                                
                                // Insert transcribed text into textarea
                                if (response && response.text) {
                                    const textarea = document.getElementById(textareaId);
                                    if (textarea) {
                                        textarea.value = response.text;
                                        // Trigger input event to activate any listeners
                                        const event = new Event('input', { bubbles: true });
                                        textarea.dispatchEvent(event);
                                    }
                                    resolve(response.text);
                                } else {
                                    // Handle empty or invalid response
                                    console.error('Invalid API response format:', response);
                                    throw new Error('Invalid response format from speech-to-text API');
                                }
                            } catch (error) {
                                console.error("Error processing audio:", error);
                                reject(error);
                            } finally {
                                if (spinner) {
                                    spinner.style.display = "none";
                                }
                            }
                        };
                        
                        this.mediaRecorder.start();
                        this.isRecording = true;
                        
                        // Update microphone icon to show it's recording
                        if (microphoneButton) {
                            microphoneButton.innerHTML = '<i class="fas fa-microphone-slash" style="font-size: 1.2rem; padding: 0.5rem; border-radius: 0.5rem; color: red; text-decoration: line-through;"></i>';
                        }
                        
                        console.log("Recording started...");
                    } catch (err) {
                        console.error("Error accessing microphone:", err);
                        reject(err);
                        if (spinner) {
                            spinner.style.display = "none";
                        }
                    }
                } else {
                    // 🛑 Stop recording
                    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
                        this.mediaRecorder.stop();
                        console.log("Recording stopped.");
                    }
                    
                    this.isRecording = false;
                    
                    // Reset microphone icon
                    if (microphoneButton) {
                        microphoneButton.innerHTML = '<i class="fas fa-microphone" style="font-size: 1.2rem; padding: 0.5rem; border-radius: 0.5rem;"></i>';
                    }
                    
                    // The rest is handled in onstop event
                }
                
                if (spinner) {
                    spinner.style.display = "none";
                }
            } catch (error) {
                console.error("General error:", error);
                reject(error);
                if (document.getElementById(spinnerContainerId)) {
                    document.getElementById(spinnerContainerId).style.display = "none";
                }
            }
        });
    }

    /**
     * Converts audio blob to WAV format with 16kHz sample rate
     * @param {Blob} audioBlob - The recorded audio blob
     * @returns {Promise<Blob>} - Promise resolving to WAV blob
     */
    async convertToWav(audioBlob) {
        const audioContext = new AudioContext({ sampleRate: 16000 });
        const arrayBuffer = await audioBlob.arrayBuffer();
        const decodedAudio = await audioContext.decodeAudioData(arrayBuffer);

        const offlineContext = new OfflineAudioContext(
            decodedAudio.numberOfChannels,
            decodedAudio.length,
            16000 // Uniform sample rate for Whisper API
        );

        const source = offlineContext.createBufferSource();
        source.buffer = decodedAudio;
        source.connect(offlineContext.destination);
        source.start();

        const renderedBuffer = await offlineContext.startRendering();
        return this.audioBufferToWav(renderedBuffer);
    }

    /**
     * Converts AudioBuffer to WAV format
     * @param {AudioBuffer} buffer - The audio buffer to convert
     * @returns {Blob} - WAV blob
     */
    audioBufferToWav(buffer) {
        const numOfChan = buffer.numberOfChannels;
        const length = buffer.length * numOfChan * 2 + 44;
        const bufferArr = new ArrayBuffer(length);
        const view = new DataView(bufferArr);
        let offset = 0;

        // Write WAV header
        // "RIFF" chunk descriptor
        this.writeString(view, offset, "RIFF"); offset += 4;
        view.setUint32(offset, 36 + buffer.length * numOfChan * 2, true); offset += 4;
        this.writeString(view, offset, "WAVE"); offset += 4;
        
        // "fmt " sub-chunk
        this.writeString(view, offset, "fmt "); offset += 4;
        view.setUint32(offset, 16, true); offset += 4; // fmt chunk size
        view.setUint16(offset, 1, true); offset += 2; // format (PCM)
        view.setUint16(offset, numOfChan, true); offset += 2; // channels
        view.setUint32(offset, buffer.sampleRate, true); offset += 4; // sample rate
        view.setUint32(offset, buffer.sampleRate * 2 * numOfChan, true); offset += 4; // byte rate
        view.setUint16(offset, numOfChan * 2, true); offset += 2; // block align
        view.setUint16(offset, 16, true); offset += 2; // bits per sample
        
        // "data" sub-chunk
        this.writeString(view, offset, "data"); offset += 4;
        view.setUint32(offset, buffer.length * numOfChan * 2, true); offset += 4;

        // Write interleaved audio data
        const channelData = [];
        for (let i = 0; i < numOfChan; i++) {
            channelData.push(buffer.getChannelData(i));
        }

        let sample;
        for (let i = 0; i < buffer.length; i++) {
            for (let ch = 0; ch < numOfChan; ch++) {
                // Clamp the sample to the range of -1 to 1
                sample = Math.max(-1, Math.min(1, channelData[ch][i]));
                // Convert to 16-bit PCM
                sample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
                view.setInt16(offset, sample, true);
                offset += 2;
            }
        }

        return new Blob([bufferArr], { type: "audio/wav" });
    }

    /**
     * Helper function to write string to DataView
     * @param {DataView} view - DataView to write to
     * @param {number} offset - Offset to write at
     * @param {string} string - String to write
     */
    writeString(view, offset, string) {
        for (let i = 0; i < string.length; i++) {
            view.setUint8(offset + i, string.charCodeAt(i));
        }
    }
}

// Export a singleton instance
export const audioRecorder = new AudioRecorder();
